import type { AxiosInstance, AxiosRequestConfig } from 'axios';
import { TokenManager } from '../../utils/auth';
import type { AuthTokens, RefreshTokenRequest } from './types';
import { ErrorHandler } from './errorHandler';

export class AuthManager {
  private httpClient: AxiosInstance;
  private errorHandler: ErrorHandler;
  private isRefreshing = false;
  private failedQueue: Array<{
    resolve: (value?: unknown) => void;
    reject: (error?: unknown) => void;
  }> = [];

  constructor(httpClient: AxiosInstance, errorHandler: ErrorHandler) {
    this.httpClient = httpClient;
    this.errorHandler = errorHandler;
  }

  /**
   * Attach authentication token to request
   */
  attachToken(config: AxiosRequestConfig): AxiosRequestConfig {
    const token = TokenManager.getToken();
    
    if (token && TokenManager.isValidToken(token)) {
      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${token}`,
      };
    }

    return config;
  }

  /**
   * Handle authentication failures and token refresh
   */
  async handleAuthFailure(error: any, originalRequest: AxiosRequestConfig & { _retry?: boolean }): Promise<any> {
    // Only handle 401 errors that haven't been retried yet
    if (error.response?.status !== 401 || originalRequest._retry) {
      throw error;
    }

    // If already refreshing, queue the request
    if (this.isRefreshing) {
      return new Promise((resolve, reject) => {
        this.failedQueue.push({ resolve, reject });
      }).then(() => {
        return this.httpClient(originalRequest);
      });
    }

    originalRequest._retry = true;
    this.isRefreshing = true;

    try {
      await this.refreshToken();
      this.processQueue(null);
      
      // Retry the original request with new token
      return this.httpClient(originalRequest);
    } catch (refreshError) {
      this.processQueue(refreshError);
      this.handleAuthenticationFailure();
      throw this.errorHandler.handleError(refreshError);
    } finally {
      this.isRefreshing = false;
    }
  }

  /**
   * Refresh the authentication token
   */
  private async refreshToken(): Promise<void> {
    const refreshToken = TokenManager.getRefreshToken();
    
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    try {
      const response = await this.httpClient.post<{ token: string; refresh_token?: string }>(
        '/api/auth/refresh',
        { refresh_token: refreshToken } as RefreshTokenRequest
      );

      const { token, refresh_token } = response.data;
      
      if (token) {
        TokenManager.setToken(token);
        
        if (refresh_token) {
          TokenManager.setRefreshToken(refresh_token);
        }
      } else {
        throw new Error('No token received from refresh endpoint');
      }
    } catch (error) {
      // Clear tokens if refresh fails
      TokenManager.clearAll();
      throw error;
    }
  }

  /**
   * Process queued requests after token refresh
   */
  private processQueue(error: unknown): void {
    this.failedQueue.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error);
      } else {
        resolve();
      }
    });

    this.failedQueue = [];
  }

  /**
   * Handle complete authentication failure
   */
  private handleAuthenticationFailure(): void {
    // Clear all authentication data
    TokenManager.clearAll();
    
    // Redirect to login page
    // In a real app, you might want to use a router or emit an event
    if (typeof window !== 'undefined') {
      window.location.href = '/login';
    }
  }

  /**
   * Check if request requires authentication
   */
  requiresAuth(config: AxiosRequestConfig): boolean {
    // Skip auth for certain endpoints or if explicitly disabled
    const skipAuth = (config as any).skipAuth;
    if (skipAuth) {
      return false;
    }

    // Skip auth for login/register endpoints
    const url = config.url || '';
    const authSkipPatterns = [
      '/api/auth/google',
      '/api/auth/refresh',
      '/api/jobs', // Public job listings
      '/api/health',
    ];

    return !authSkipPatterns.some(pattern => url.includes(pattern));
  }

  /**
   * Get current authentication status
   */
  isAuthenticated(): boolean {
    const token = TokenManager.getToken();
    return token !== null && TokenManager.isValidToken(token);
  }

  /**
   * Get current user token
   */
  getToken(): string | null {
    return TokenManager.getToken();
  }

  /**
   * Set authentication tokens
   */
  setTokens(tokens: AuthTokens): void {
    TokenManager.setToken(tokens.accessToken);
    
    if (tokens.refreshToken) {
      TokenManager.setRefreshToken(tokens.refreshToken);
    }
  }

  /**
   * Clear all authentication data
   */
  clearAuth(): void {
    TokenManager.clearAll();
  }

  /**
   * Handle logout
   */
  async logout(): Promise<void> {
    try {
      // Attempt to notify server of logout
      await this.httpClient.post('/api/auth/logout');
    } catch (error) {
      // Continue with logout even if server request fails
      this.errorHandler.logError(
        this.errorHandler.handleError(error),
        'Logout server notification failed'
      );
    } finally {
      this.clearAuth();
    }
  }
}